// SecureTokenStorage.js
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { isCapacitor } from '../utils/platform';
import { createLogger } from './Debug';
const TOKEN_KEY = 'voxia_secure_tokens';
const BACKUP_PREFIX = 'backup_';

class SecureTokenStorage {
  constructor() {
    this.isAvailable = false;
    this.lastBackupTime = 0;
    this.checkAvailability();
  }

  async checkAvailability() {
    const log = createLogger('SecureTokenStorage.checkAvailability');
    if (!isCapacitor) {
      this.isAvailable = false;
      return;
    }

    try {
      const result = await NativeBiometric.isAvailable();
      this.isAvailable = result.isAvailable;
      log.debug('[SecureTokenStorage] Biometric available:', this.isAvailable);
    } catch (error) {
      
      log.error('[SecureTokenStorage] Error checking availability:', error);
      this.isAvailable = false;
    }
  }

  // Store tokens securely with biometric protection
  async storeSecureTokens(accessToken, refreshToken, expiration) {
    const log = createLogger('SecureTokenStorage.storeSecureTokens');
    if (!this.isAvailable) {
      log.debug('[SecureTokenStorage] Biometric not available, using backup storage');
      return this.storeBackupTokens(accessToken, refreshToken, expiration);
    }

    try {
      const tokenData = {
        accessToken,
        refreshToken,
        expiration,
        timestamp: Date.now()
      };

      // Store in secure keychain
      await NativeBiometric.setCredentials({
        username: TOKEN_KEY,
        password: JSON.stringify(tokenData),
        server: 'com.gbmmedical.voxia'
      });

      log.debug('[SecureTokenStorage] Tokens stored securely in keychain');
      
      // Also store a backup in Preferences (encrypted)
      await this.storeBackupTokens(accessToken, refreshToken, expiration);
      
      return true;
    } catch (error) {
      log.error('[SecureTokenStorage] Error storing secure tokens:', error);
      // Fallback to backup storage
      return this.storeBackupTokens(accessToken, refreshToken, expiration);
    }
  }

  // Retrieve tokens from secure storage
  async getSecureTokens() {
    const log = createLogger('SecureTokenStorage.getSecureTokens');
    if (!this.isAvailable) {
      log.debug('[SecureTokenStorage] Biometric not available, using backup storage');
      return this.getBackupTokens();
    }

    try {
      const credentials = await NativeBiometric.getCredentials({
        server: 'com.gbmmedical.voxia'
      });

      if (credentials && credentials.password) {
        const tokenData = JSON.parse(credentials.password);
        log.debug('[SecureTokenStorage] Retrieved tokens from keychain');
        
        // Validate token age (max 30 days)
        const age = Date.now() - tokenData.timestamp;
        if (age > 30 * 24 * 60 * 60 * 1000) {
            log.warn('[SecureTokenStorage] Tokens are too old, clearing');
          await this.clearSecureTokens();
          return null;
        }
        
        return tokenData;
      }
    } catch (error) {
      log.error('[SecureTokenStorage] Error retrieving secure tokens:', error);
    }

    // Fallback to backup
    return this.getBackupTokens();
  }

  // Store encrypted backup in Preferences
  async storeBackupTokens(accessToken, refreshToken, expiration) {
    const log = createLogger('SecureTokenStorage.storeBackupTokens');
    try {
      const backup = {
        a: this.simpleEncrypt(accessToken),
        r: this.simpleEncrypt(refreshToken),
        e: expiration,
        t: Date.now()
      };

      await Preferences.set({
        key: BACKUP_PREFIX + 'tokens',
        value: JSON.stringify(backup)
      });

      this.lastBackupTime = Date.now();
      log.debug('[SecureTokenStorage] Backup tokens stored');
      return true;
    } catch (error) {
      log.error('[SecureTokenStorage] Error storing backup tokens:', error);
      return false;
    }
  }

  // Retrieve backup tokens
  async getBackupTokens() {
    const log = createLogger('SecureTokenStorage.getBackupTokens');
    try {
      const { value } = await Preferences.get({ key: BACKUP_PREFIX + 'tokens' });
      if (!value) return null;

      const backup = JSON.parse(value);
      
      // Check age
      const age = Date.now() - backup.t;
      if (age > 30 * 24 * 60 * 60 * 1000) {
        log.warn('[SecureTokenStorage] Backup tokens too old');
        return null;
      }

      return {
        accessToken: this.simpleDecrypt(backup.a),
        refreshToken: this.simpleDecrypt(backup.r),
        expiration: backup.e,
        timestamp: backup.t
      };
    } catch (error) {
      log.error('[SecureTokenStorage] Error retrieving backup tokens:', error);
      return null;
    }
  }

  // Clear all secure tokens
  async clearSecureTokens() {
    const log = createLogger('SecureTokenStorage.clearSecureTokens');
    try {
      if (this.isAvailable) {
        await NativeBiometric.deleteCredentials({
          server: 'com.gbmmedical.voxia'
        });
      }
      
      await Preferences.remove({ key: BACKUP_PREFIX + 'tokens' });
      log.debug('[SecureTokenStorage] All secure tokens cleared');
    } catch (error) {
      log.error('[SecureTokenStorage] Error clearing secure tokens:', error);
    }
  }

  // Simple encryption for backup (not cryptographically secure, but better than plaintext)
  simpleEncrypt(text) {
    const log = createLogger('SecureTokenStorage.simpleEncrypt');
    if (!text) return '';
    const key = 'voxia2024secure';
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(result); // Base64 encode
  }

  simpleDecrypt(encrypted) {
    const log = createLogger('SecureTokenStorage.simpleDecrypt');
      if (!encrypted) return '';
    const key = 'voxia2024secure';
    const text = atob(encrypted); // Base64 decode
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  }
}

export const secureTokenStorage = new SecureTokenStorage();